import { create } from "zustand";
import { env } from "@/env";

/**
 * Power-user mode: an orthogonal-to-role gate that unlocks CRUD on
 * findings, assets, and briefings. The passphrase lives in
 * VITE_POWER_USER_PASSPHRASE (env-only — never hardcoded in source).
 *
 * Persists per-tab in sessionStorage so the unlock survives route changes
 * but does NOT survive reloads (closing the tab is the deliberate "lock"
 * gesture).
 */

type PowerUserState = {
  isUnlocked: boolean;
  attemptUnlock: (passphrase: string) => boolean;
  lock: () => void;
};

const STORAGE_KEY = "xaes-power";

function readInitial(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeUnlocked(unlocked: boolean) {
  try {
    if (unlocked) {
      window.sessionStorage.setItem(STORAGE_KEY, "1");
    } else {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* sessionStorage unavailable — degrade silently */
  }
}

export const usePowerUser = create<PowerUserState>((set) => ({
  isUnlocked: readInitial(),
  attemptUnlock: (passphrase) => {
    const ok = passphrase === env.VITE_POWER_USER_PASSPHRASE;
    if (ok) {
      writeUnlocked(true);
      set({ isUnlocked: true });
    }
    return ok;
  },
  lock: () => {
    writeUnlocked(false);
    set({ isUnlocked: false });
  },
}));
